/**
 * Operator-facing connector status on Integrations > Connectors.
 *
 * STATUS-TRUTH-001: four independent facts, never merged into one optimistic badge.
 *
 *  1. authorisation  — `auth_status` (OAuth/credential evidence only)
 *  2. connection     — persisted trigger-derived `connection_status`
 *  3. health         — `health_status` (affirmative probe evidence only)
 *  4. automatic sync — `enabled` (queue gating, NOT a connection/circuit breaker)
 *
 * Rules enforced here (no provider calls, no connector writes):
 *  - A positive health indicator requires affirmative `health_status = healthy`.
 *    Authorisation alone is never health evidence.
 *  - Persisted `connection_status` is the connection source. It is only rebuilt
 *    from auth_status when the persisted value cannot carry connection truth
 *    (`disabled` is the database mirror of `enabled = false`) or when affirmative
 *    auth evidence contradicts it. A missing/unreadable value is "Not reported".
 *  - Generic activity timestamps (`last_sync_at`, generic sync_jobs completion)
 *    never prove inventory/price/publication success. Only a domain-specific
 *    completed-success record does, otherwise "Last successful sync not reported".
 */

import { connectionStatusMeta, deriveConnectionStatus, type ConnectionStatus } from "@/lib/connectors";
import { connectionAttemptIsLive, merchantConnectionLabel } from "@/lib/merchant-connection-status";

export const AUTHENTICATED_AUTH_STATUSES = ["authorized", "connected"] as const;

/** Affirmative auth evidence that the stored connection state is out of date. */
const AUTH_FAILURE_STATUSES = ["expired", "revoked", "error", "degraded"] as const;

export type ConnectorAttention =
  | { required: false; message: null; actionLabel: null }
  | { required: true; message: string; actionLabel: "Reconnect" | null };

export interface ConnectorStatusFields {
  id: string;
  enabled: boolean;
  auth_status: string | null | undefined;
  health_status: string | null | undefined;
  connection_error: string | null | undefined;
  last_sync_at: string | null | undefined;
  /** Persisted, trigger-derived connection state. Missing/unreadable => Not reported. */
  connection_status?: string | null;
  last_sync_attempt_at?: string | null;
  /** OAuth/connect start. Marketplace sync uses last_sync_attempt_at and must not drive this. */
  auth_attempted_at?: string | null;
}

/**
 * Evidence supplied from existing read contracts (no new RPC/query surface).
 * `lastDomainSuccessAt` must come from a domain-specific completed-success
 * record, never from a generic connector activity timestamp.
 */
export interface ConnectorStatusEvidence {
  lastDomainSuccessAt?: string | null;
  lastActivityAt?: string | null;
}

export type ConnectionStateSource = "persisted" | "authorization" | "not_reported";

function norm(value: string | null | undefined): string {
  return (value ?? "").trim();
}

const CONNECTION_STATUS_VALUES: readonly ConnectionStatus[] = [
  "disconnected",
  "connecting",
  "connected",
  "degraded",
  "disabled",
];

export function isAuthenticatedAuthStatus(authStatus: string | null | undefined): boolean {
  const a = norm(authStatus);
  return a === "authorized" || a === "connected";
}

export function isAuthFailureStatus(authStatus: string | null | undefined): boolean {
  const a = norm(authStatus);
  return (AUTH_FAILURE_STATUSES as readonly string[]).includes(a);
}

/** Returns the persisted connection value only when it is a recognised state. */
export function parsePersistedConnectionStatus(
  connectionStatus: string | null | undefined,
): ConnectionStatus | null {
  const value = norm(connectionStatus);
  return (CONNECTION_STATUS_VALUES as readonly string[]).includes(value)
    ? (value as ConnectionStatus)
    : null;
}

/**
 * Authorization / live connection — ignores `enabled`.
 * `deriveConnectionStatus` still treats enabled=false as "disabled" (DB mirror).
 */
export function deriveAuthorizationStatus(
  authStatus: string | null | undefined,
  healthStatus: string | null | undefined,
): Exclude<ConnectionStatus, "disabled"> {
  const derived = deriveConnectionStatus(true, authStatus, healthStatus);
  return derived === "disabled" ? "disconnected" : derived;
}

export function automaticSyncPresentation(enabled: boolean): { on: boolean; label: string; badge: string } {
  return enabled
    ? { on: true, label: "Automatic sync on", badge: "active" }
    : { on: false, label: "Automatic sync off", badge: "inactive" };
}

export function currentConnectorAttention(input: {
  auth_status: string | null | undefined;
  health_status: string | null | undefined;
  connection_error: string | null | undefined;
  auth_attempted_at?: string | null;
}): ConnectorAttention {
  const auth = norm(input.auth_status);
  const health = norm(input.health_status);
  const err = norm(input.connection_error);
  const attemptLive = connectionAttemptIsLive(input.auth_attempted_at);

  if (isAuthenticatedAuthStatus(auth)) {
    // A later successful auth state clears historical connection_error.
    return { required: false, message: null, actionLabel: null };
  }

  if (auth === "expired" || auth === "revoked") {
    return { required: true, message: "Reauthorization required", actionLabel: "Reconnect" };
  }

  if (auth === "not_configured" || auth === "") {
    return { required: true, message: "Requires credentials / authorization", actionLabel: null };
  }

  if (auth === "error" || auth === "degraded" || health === "error") {
    return {
      required: true,
      message: err || "Attention required",
      actionLabel: null,
    };
  }

  if (auth === "configured" || ((auth === "connecting" || auth === "pending") && !attemptLive)) {
    return { required: true, message: "Authentication required", actionLabel: "Reconnect" };
  }

  return { required: false, message: null, actionLabel: null };
}

/**
 * Provider health. A positive tone is ONLY produced by affirmative
 * `health_status = healthy` evidence. Unknown/absent health is never "Healthy",
 * and unknown health is never treated as failed authentication.
 */
export function healthPresentation(input: {
  auth_status: string | null | undefined;
  health_status: string | null | undefined;
}): { label: string; tone: "positive" | "neutral" | "warning" | "danger" } {
  const health = norm(input.health_status);
  if (health === "healthy") return { label: "Healthy", tone: "positive" };
  if (health === "error" || health === "unhealthy" || health === "failed") {
    return { label: "Unhealthy", tone: "danger" };
  }
  if (health === "degraded" || health === "warning") return { label: "Degraded", tone: "warning" };
  // Missing health evidence says nothing about authorisation or provider uptime.
  return { label: "Health not reported", tone: "neutral" };
}

/**
 * Connection state sourced from the persisted trigger-derived `connection_status`.
 *
 * `disabled` is the database mirror of `enabled = false`; it carries no
 * connection truth, so the authorization fields are used instead and the
 * automatic-sync state is reported separately. A missing/unreadable persisted
 * value is reported as "Not reported" — never inferred from auth alone.
 */
export function resolveConnectionState(connector: {
  connection_status?: string | null;
  auth_status?: string | null;
  health_status?: string | null;
  auth_attempted_at?: string | null;
}): {
  status: ConnectionStatus | null;
  label: string;
  badge: string;
  icon: string;
  tone: "positive" | "neutral" | "warning" | "danger" | "info";
  source: ConnectionStateSource;
} {
  const persisted = parsePersistedConnectionStatus(connector.connection_status);
  const auth = connector.auth_status;
  const attemptLive = connectionAttemptIsLive(connector.auth_attempted_at);

  // A stored in-progress badge is not evidence that OAuth is still running.
  // Generic marketplace sync timestamps deliberately do not extend this window.
  const awaitingAuth = norm(auth) === "configured"
    || ((norm(auth) === "connecting" || norm(auth) === "pending") && !attemptLive);
  if (persisted === "connecting" && awaitingAuth) {
    return {
      ...connectionStatusMeta("disconnected"),
      label: merchantConnectionLabel(auth, connector.health_status, { attemptLive }),
      source: "authorization",
    };
  }

  // Affirmative auth failure always wins over a stale persisted "connected".
  if (persisted && persisted !== "disabled" && !isAuthFailureStatus(auth)) {
    const meta = connectionStatusMeta(persisted);
    return { ...meta, source: "persisted" };
  }

  if (persisted === "disabled" || isAuthFailureStatus(auth)) {
    const derived = deriveAuthorizationStatus(auth, connector.health_status);
    const meta = connectionStatusMeta(derived);
    return {
      ...meta,
      label: merchantConnectionLabel(auth, connector.health_status, { attemptLive }),
      source: "authorization",
    };
  }

  return {
    status: null,
    label: "Not reported",
    badge: "inactive",
    icon: "ri-question-line",
    tone: "neutral",
    source: "not_reported",
  };
}

export function latestTimestamp(...values: Array<string | null | undefined>): string | null {
  let best: string | null = null;
  let bestMs = Number.NEGATIVE_INFINITY;
  for (const raw of values) {
    if (!raw) continue;
    const ms = Date.parse(raw);
    if (Number.isNaN(ms)) continue;
    if (ms > bestMs) {
      bestMs = ms;
      best = raw;
    }
  }
  return best;
}

export const LAST_SUCCESS_NOT_REPORTED = "Last successful sync not reported";

export function presentConnectorStatus(
  connector: ConnectorStatusFields,
  evidence: ConnectorStatusEvidence = {},
) {
  const connection = resolveConnectionState(connector);
  const automaticSync = automaticSyncPresentation(connector.enabled);
  const attention = currentConnectorAttention(connector);
  const health = healthPresentation(connector);

  // Domain success requires a domain-specific completed-success record.
  // Generic `last_sync_at` / job completion is activity only.
  const lastSuccessfulSyncAt = latestTimestamp(evidence.lastDomainSuccessAt);
  const lastActivityAt = latestTimestamp(evidence.lastActivityAt, connector.last_sync_at);

  return {
    /** Primary operator badge. Same object exposed twice for explicit intent. */
    connection,
    authorization: connection,
    connectionSource: connection.source,
    automaticSync,
    attention,
    health,
    lastSuccessfulSyncAt,
    lastSuccessfulSyncCaption: "Last successful sync",
    lastSuccessfulSyncUnavailableLabel: LAST_SUCCESS_NOT_REPORTED,
    lastActivityAt,
    lastActivityCaption: "Last connector activity",
  };
}

/**
 * Latest domain-specific completed-success timestamp per connector.
 * Callers must pass rows from a domain-success record (e.g. a listing that was
 * actually published), never generic connector/job activity.
 */
export function lastDomainSuccessAtByConnector(
  rows: Array<{ connector_id: string | null | undefined; last_published_at?: string | null }>,
): Map<string, string> {
  const map = new Map<string, string>();
  for (const row of rows) {
    const connectorId = norm(row.connector_id);
    if (!connectorId) continue;
    const next = latestTimestamp(map.get(connectorId), row.last_published_at);
    if (next) map.set(connectorId, next);
  }
  return map;
}

/**
 * Generic connector activity aggregation (sync_jobs completion). This is
 * activity evidence only and must never be presented as domain success.
 */
export function lastSuccessfulJobAtByConnector(
  jobs: Array<{ connector_id: string | null; completed_at: string | null; status?: string | null }>,
): Map<string, string> {
  const map = new Map<string, string>();
  for (const job of jobs) {
    if (!job.connector_id || !job.completed_at) continue;
    const prev = map.get(job.connector_id);
    const next = latestTimestamp(prev, job.completed_at);
    if (next) map.set(job.connector_id, next);
  }
  return map;
}